import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  rosterApi,
  tournamentApi,
  leagueApi,
  Roster,
  TournamentDetails,
  Wrestler,
  RosterWrestler,
} from '../services/api-client';
import { useToast } from '../contexts/ToastContext';
import { MobileLayout } from '../components/layout/MobileLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { Modal } from '../components/Modal';

export default function RosterSelectionPage() {
  const { leagueId, memberId } = useParams<{ leagueId: string; memberId: string }>();
  const { showToast } = useToast();

  // Data state
  const [roster, setRoster] = useState<Roster | null>(null);
  const [tournament, setTournament] = useState<TournamentDetails | null>(null);
  const [leagueBudget, setLeagueBudget] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [activeWeight, setActiveWeight] = useState<string | null>(null);
  const [savingWeights, setSavingWeights] = useState<Set<string>>(new Set());
  const [showPreliminarySeedsModal, setShowPreliminarySeedsModal] = useState(false);

  useEffect(() => {
    if (leagueId && memberId) {
      loadData();
    }
  }, [leagueId, memberId]);

  const loadData = async () => {
    if (!leagueId || !memberId) return;

    try {
      setLoading(true);
      setError(null);

      // Load league to get tournament ID and budget
      const leagueData = await leagueApi.get(leagueId);
      setLeagueBudget(leagueData.budgetPerTeam);

      // Check if rosters are locked
      if (leagueData.rosterLocked) {
        setError(
          'Rosters are locked. No changes can be made. Contact your league administrator if you believe this is an error.'
        );
        return;
      }

      if (!leagueData.tournamentId) {
        setError('This league is not linked to a tournament');
        return;
      }

      // Load tournament with wrestlers
      const tournamentData = await tournamentApi.get(leagueData.tournamentId);

      // If weightClasses is empty, derive from wrestler data
      if (tournamentData.weightClasses.length === 0 && tournamentData.wrestlersByWeight) {
        const derivedWeightClasses = Object.keys(tournamentData.wrestlersByWeight).sort(
          (a, b) => parseInt(a) - parseInt(b)
        );
        tournamentData.weightClasses = derivedWeightClasses;
      }

      setTournament(tournamentData);

      // Set first weight class as active
      if (tournamentData.weightClasses.length > 0 && !activeWeight) {
        setActiveWeight(tournamentData.weightClasses[0]);
      }

      // Load current roster
      const rosterData = await rosterApi.get(leagueId, memberId);
      setRoster(rosterData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getWrestlerCost = (wrestler: Wrestler): number => {
    if (!tournament || wrestler.seed === null) return 0;
    const index = wrestler.seed - 1;
    if (index < 0 || index >= tournament.placePoints.length) return 0;
    return tournament.placePoints[index];
  };

  const handleWrestlerClick = async (wrestler: Wrestler) => {
    if (!leagueId || !memberId || !roster || !tournament || !activeWeight) return;

    const cost = getWrestlerCost(wrestler);
    const existingSelection = roster.wrestlers.find((w) => w.weightClass === activeWeight);
    const isSelected = existingSelection?.wrestlerId === wrestler.id;

    // If clicking the already-selected wrestler, deselect it
    if (isSelected) {
      setRoster((prev) => ({
        ...prev!,
        wrestlers: prev!.wrestlers.filter((w) => w.weightClass !== activeWeight),
        totalCost: prev!.totalCost - existingSelection!.wrestlerCost,
        totalPoints: prev!.totalPoints - existingSelection!.wrestlerTeamPoints,
      }));
      setSavingWeights((prev) => new Set(prev).add(activeWeight));

      try {
        await rosterApi.removeWrestler(leagueId, memberId, activeWeight);
        showToast('Wrestler removed from roster', 'info');
      } catch (err) {
        setRoster((prev) => ({
          ...prev!,
          wrestlers: [...prev!.wrestlers, existingSelection!],
          totalCost: prev!.totalCost + existingSelection!.wrestlerCost,
          totalPoints: prev!.totalPoints + existingSelection!.wrestlerTeamPoints,
        }));
        showToast(err instanceof Error ? err.message : 'Failed to remove wrestler', 'error');
      } finally {
        setSavingWeights((prev) => {
          const next = new Set(prev);
          next.delete(activeWeight);
          return next;
        });
      }
      return;
    }

    // Budget check: account for current selection being freed up
    const effectiveBudgetRemaining =
      leagueBudget - roster.totalCost + (existingSelection?.wrestlerCost || 0);
    if (cost > effectiveBudgetRemaining) {
      showToast(
        `Budget exceeded! This wrestler costs ${cost} but you only have ${effectiveBudgetRemaining} remaining.`,
        'error'
      );
      return;
    }

    // Optimistically create the new roster wrestler
    const optimisticWrestler: RosterWrestler = {
      id: 'temp-' + Date.now(),
      leagueMemberId: memberId,
      wrestlerId: wrestler.id,
      weightClass: wrestler.weightClass,
      wrestlerCost: cost,
      createdAt: new Date().toISOString(),
      wrestlerName: wrestler.name,
      wrestlerSeed: wrestler.seed,
      wrestlerSchoolTeam: wrestler.schoolTeam,
      wrestlerPlace: wrestler.place,
      wrestlerTeamPoints: wrestler.teamPoints,
      wrestlerMatchResults: wrestler.matchResults,
    };

    // Optimistically update UI (remove old if swapping, add new)
    setRoster((prev) => {
      const withoutOld = existingSelection
        ? prev!.wrestlers.filter((w) => w.weightClass !== activeWeight)
        : prev!.wrestlers;
      const costDelta = cost - (existingSelection?.wrestlerCost || 0);
      const pointsDelta = wrestler.teamPoints - (existingSelection?.wrestlerTeamPoints || 0);
      return {
        ...prev!,
        wrestlers: [...withoutOld, optimisticWrestler],
        totalCost: prev!.totalCost + costDelta,
        totalPoints: prev!.totalPoints + pointsDelta,
      };
    });

    setSavingWeights((prev) => new Set(prev).add(wrestler.weightClass));

    try {
      // If swapping, remove old first
      if (existingSelection) {
        await rosterApi.removeWrestler(leagueId, memberId, activeWeight);
      }

      const savedWrestler = await rosterApi.selectWrestler(leagueId, memberId, wrestler.id);

      setRoster((prev) => ({
        ...prev!,
        wrestlers: prev!.wrestlers.map((w) => (w.id === optimisticWrestler.id ? savedWrestler : w)),
      }));

      showToast(`${wrestler.name} added to roster!`, 'success');
    } catch (err) {
      // Revert: remove optimistic new, restore old if it was a swap
      setRoster((prev) => {
        const withoutOptimistic = prev!.wrestlers.filter((w) => w.id !== optimisticWrestler.id);
        const costDelta = cost - (existingSelection?.wrestlerCost || 0);
        const pointsDelta = wrestler.teamPoints - (existingSelection?.wrestlerTeamPoints || 0);
        return {
          ...prev!,
          wrestlers: existingSelection
            ? [...withoutOptimistic, existingSelection]
            : withoutOptimistic,
          totalCost: prev!.totalCost - costDelta,
          totalPoints: prev!.totalPoints - pointsDelta,
        };
      });

      showToast(err instanceof Error ? err.message : 'Failed to select wrestler', 'error');
    } finally {
      setSavingWeights((prev) => {
        const next = new Set(prev);
        next.delete(wrestler.weightClass);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <MobileLayout>
        <PageHeader title="Manage Roster" showBack />
        <div className="flex justify-center items-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading roster...</p>
          </div>
        </div>
      </MobileLayout>
    );
  }

  if (error || !tournament || !roster) {
    return (
      <MobileLayout>
        <PageHeader title="Manage Roster" showBack />
        <div className="p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error || 'Failed to load data'}</p>
          </div>
        </div>
      </MobileLayout>
    );
  }

  const budgetRemaining = leagueBudget - roster.totalCost;
  const budgetPercentage = (roster.totalCost / leagueBudget) * 100;
  const currentSelection = activeWeight
    ? roster.wrestlers.find((w) => w.weightClass === activeWeight)
    : null;
  const availableWrestlers = activeWeight ? tournament.wrestlersByWeight[activeWeight] || [] : [];
  const isSaving = activeWeight ? savingWeights.has(activeWeight) : false;
  const hasSeeds = Object.values(tournament.wrestlersByWeight).some((wrestlers) =>
    wrestlers.some((w) => w.seed !== null)
  );

  return (
    <MobileLayout>
      <div className="flex flex-col h-dvh overflow-hidden">
        {/* Top section - stays put while wrestler list scrolls below */}
        <div className="flex-shrink-0">
          <PageHeader
            title="Manage Roster"
            showBack
            actions={
              <span className="text-xs text-gray-500">
                {roster.wrestlers.length}/{tournament.weightClasses.length}
              </span>
            }
          />
          {/* Budget Bar - Compact */}
          <div className="bg-white border-b border-gray-200 shadow-sm">
            <div className="px-4 py-3">
              <div className="flex justify-between items-center text-sm mb-2">
                <span className="text-gray-600">
                  Budget: ${roster.totalCost}/${leagueBudget}
                </span>
                <span
                  className={`font-bold ${budgetRemaining < 0 ? 'text-red-600' : 'text-green-600'}`}
                >
                  {budgetRemaining} left
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ease-in-out ${
                    budgetPercentage > 100
                      ? 'bg-red-500'
                      : budgetPercentage > 80
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(Math.max(budgetPercentage, 0), 100).toFixed(1)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Seeds Status Banner */}
          {tournament.seedsArePreliminary ? (
            <div className="bg-amber-50 border-y border-amber-200 px-4 py-3">
              <div className="flex items-start gap-2">
                <span className="text-amber-600 text-lg flex-shrink-0">⚠️</span>
                <div className="flex-1 text-sm text-amber-800">
                  <p className="font-semibold">
                    Seeds are preliminary and subject to change when official brackets are released.
                    <button
                      onClick={() => setShowPreliminarySeedsModal(true)}
                      className="ml-2 text-amber-900 hover:text-amber-950 underline focus:outline-none"
                      aria-label="Learn more about preliminary seeds"
                    >
                      Learn more
                    </button>
                  </p>
                </div>
              </div>
            </div>
          ) : !hasSeeds ? (
            <div className="bg-gray-50 border-y border-gray-200 px-4 py-3">
              <div className="flex items-start gap-2">
                <span className="text-gray-400 text-lg flex-shrink-0">🕐</span>
                <div className="flex-1 text-sm text-gray-600">
                  <p className="font-semibold">Seeds coming soon.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border-y border-green-200 px-4 py-3">
              <div className="flex items-start gap-2">
                <span className="text-green-600 text-lg flex-shrink-0">✅</span>
                <div className="flex-1 text-sm text-green-800">
                  <p className="font-semibold">
                    Seeds are official.
                    <button
                      onClick={() => setShowPreliminarySeedsModal(true)}
                      className="ml-2 text-green-900 hover:text-green-950 underline focus:outline-none"
                      aria-label="Learn more about seeds"
                    >
                      Learn more
                    </button>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* External Resource Links */}
          {(tournament.floUrl || tournament.wrestlestatUrl) && (
            <div className="bg-white border-b border-gray-200 px-4 py-2">
              <div className="flex flex-wrap justify-center gap-2">
                {tournament.floUrl && (
                  <a
                    href={tournament.floUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-white text-xs font-medium bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-sm transition-all"
                  >
                    Follow Brackets
                  </a>
                )}
                {tournament.wrestlestatUrl && (
                  <a
                    href={tournament.wrestlestatUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-white text-xs font-medium bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-sm transition-all"
                  >
                    Wrestler Stats
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Horizontal Scrollable Weight Class Pills */}
          <div className="bg-white border-b border-gray-200">
            <div className="overflow-x-auto max-md:scrollbar-hide">
              <div className="flex gap-2 px-4 py-3 min-w-min">
                {tournament.weightClasses.map((wc) => {
                  const hasSelection = roster.wrestlers.some((w) => w.weightClass === wc);
                  const isActive = activeWeight === wc;
                  const isSavingThis = savingWeights.has(wc);

                  return (
                    <button
                      key={wc}
                      onClick={() => setActiveWeight(wc)}
                      className={`
                    flex-shrink-0 px-4 py-2 rounded-full font-medium text-sm transition-all whitespace-nowrap
                    ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : hasSelection
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }
                  `}
                    >
                      <span>{wc} lbs</span>
                      {isSavingThis ? (
                        <span className="ml-1 animate-spin">⏳</span>
                      ) : hasSelection ? (
                        <span className="ml-1">✓</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* end top section */}

        {/* Wrestler Selection Content - scrolls independently */}
        <div className="flex-1 overflow-y-auto pb-16">
          {activeWeight && (
            <div>
              <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900">{activeWeight} lbs</h2>
                  <span className="text-xs text-gray-500">
                    {availableWrestlers.length} available
                  </span>
                </div>
              </div>
              <div className="px-4 py-4">
                {/* All Wrestlers - Sorted by Seed */}
                <div className="space-y-3">
                  {availableWrestlers.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      No wrestlers available for this weight class
                    </p>
                  ) : (
                    [...availableWrestlers]
                      .sort((a, b) => {
                        // Sort by seed: seeded first (ascending), then unseeded
                        if (a.seed === null && b.seed === null) return 0;
                        if (a.seed === null) return 1;
                        if (b.seed === null) return -1;
                        return a.seed - b.seed;
                      })
                      .map((wrestler) => {
                        const cost = getWrestlerCost(wrestler);
                        const isSelected = currentSelection?.wrestlerId === wrestler.id;
                        // Account for current selection being freed up when calculating affordability
                        const effectiveBudgetRemaining =
                          leagueBudget - roster.totalCost + (currentSelection?.wrestlerCost || 0);
                        const wouldExceedBudget = !isSelected && cost > effectiveBudgetRemaining;
                        const isClickable = isSelected || (!wouldExceedBudget && !isSaving);

                        // Shorten name to "F. LastName"
                        const nameParts = wrestler.name.split(' ');
                        const shortName =
                          nameParts.length > 1
                            ? `${nameParts[0][0]}. ${nameParts[nameParts.length - 1]}`
                            : wrestler.name;

                        // Truncate team if too long
                        const team = wrestler.schoolTeam || 'Unknown';
                        const shortTeam = team.length > 15 ? team.substring(0, 15) + '...' : team;

                        return (
                          <div
                            key={wrestler.id}
                            onClick={() => isClickable && handleWrestlerClick(wrestler)}
                            className={`
                        border rounded-lg px-3 py-2 transition-all flex items-center gap-2
                        ${
                          isSelected
                            ? 'bg-green-50 border-green-500 border-2'
                            : wouldExceedBudget
                              ? 'bg-gray-50 border-gray-200 opacity-60'
                              : 'bg-white border-gray-200 active:bg-blue-50'
                        }
                        ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}
                      `}
                          >
                            {/* Compact single line */}
                            <div className="flex items-center gap-2 flex-1 min-w-0 text-sm">
                              {isSelected && (
                                <span className="text-green-600 text-base flex-shrink-0">✓</span>
                              )}
                              {wrestler.seed && (
                                <span
                                  className={`font-semibold flex-shrink-0 ${isSelected ? 'text-green-900' : 'text-gray-900'}`}
                                >
                                  #{wrestler.seed}
                                </span>
                              )}
                              <span
                                className={`font-medium truncate ${isSelected ? 'text-green-900' : 'text-gray-900'}`}
                              >
                                {shortName}
                              </span>
                              <span className="text-gray-500 flex-shrink-0">•</span>
                              <span className="text-gray-600 truncate">{shortTeam}</span>
                              <span
                                className={`font-semibold ml-auto flex-shrink-0 ${wouldExceedBudget ? 'text-red-600' : 'text-gray-900'}`}
                              >
                                ${cost}
                              </span>
                            </div>

                            {isSaving && isSelected && (
                              <span className="flex-shrink-0 text-xs text-gray-400">...</span>
                            )}
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Seeds Info Modal */}
      <Modal
        isOpen={showPreliminarySeedsModal}
        onClose={() => setShowPreliminarySeedsModal(false)}
        title={tournament.seedsArePreliminary ? 'About Preliminary Seeds' : 'About Seeds'}
      >
        <div className="space-y-4 text-gray-700">
          {tournament.seedsArePreliminary ? (
            <p>
              These seeds are preliminary and will be updated when official tournament brackets are
              released. Wrestler costs are based on seeds, so costs may change.
            </p>
          ) : (
            <p>
              Seeds are official and reflect the tournament brackets. Wrestler costs are based on
              these seeds.
            </p>
          )}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Budget Protection:</h3>
            <p>
              If seed changes cause your roster to exceed the budget, your current roster remains
              valid and you can keep it without changes.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Making Changes Later:</h3>
            <p>
              If you want to make any roster changes after seeds change, you must be within budget
              at that time. This protects early participants while maintaining fairness for
              everyone.
            </p>
          </div>
        </div>
      </Modal>
    </MobileLayout>
  );
}
